import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import './ApplicationSignup.css';

const resolveApiBaseUrl = () => {
  const envBase = process.env.REACT_APP_API_BASE_URL;
  if (envBase && envBase.trim()) return envBase.trim().replace(/\/+$/, '');
  if (typeof window !== 'undefined' && window.location?.hostname) {
    const host = window.location.hostname;
    if (host === 'localhost' || host === '127.0.0.1' || host === '::1' || host === '[::1]') {
      return 'http://127.0.0.1:8000/api';
    }
    return `http://${host}:8000/api`;
  }
  return 'http://127.0.0.1:8000/api';
};

const API_BASE_URL = resolveApiBaseUrl();
const apiUrl = (path = '') => `${API_BASE_URL}/${String(path).replace(/^\/+/, '')}`;
const OWNER_PRIMARY_FIELDS = [
  ['username', 'Username'],
  ['first_name', 'First name'],
  ['last_name', 'Last name'],
  ['email', 'Email'],
  ['phone_number', 'Phone number'],
  ['dob', 'DOB'],
  ['zip_code', 'ZIP code'],
  ['business_email', 'Business email'],
];

const OWNER_COMPANY_FIELDS = [
  ['requested_company_name', 'Public company name'],
  ['requested_company_slug', 'Company URL slug'],
  ['business_phone', 'Business phone'],
  ['legal_business_name', 'Legal business name'],
  ['business_address', 'Business address'],
  ['website_url', 'Website URL'],
  ['years_in_business', 'Years in business'],
  ['tax_identifier_last4', 'Tax ID (last 4 digits)'],
];

const SignUpOwner = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    username: '', first_name: '', last_name: '', email: '', phone_number: '', dob: '', zip_code: '',
    password: '', confirm_password: '',
    requested_company_name: '', requested_company_slug: '', business_email: '', business_phone: '',
    legal_business_name: '', business_address: '', website_url: '', years_in_business: '', tax_identifier_last4: '', message: '',
  });
  const [message, setMessage] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    try {
      await axios.post(apiUrl('register-owner-application/'), {
        ...formData,
        years_in_business: formData.years_in_business === '' ? null : Number(formData.years_in_business),
      });
      setMessage('Owner application submitted. You can log in after approval.');
      setTimeout(() => navigate('/login'), 900);
    } catch (error) {
      setMessage(error?.response?.data?.error || 'Unable to submit owner application.');
    }
  };

  return (
    <div className="application-shell">
      <div className="application-card">
        <header className="application-header">
          <h1>Business Owner Application</h1>
          <p>
            Set up your company profile and ownership request. Once reviewed, you can manage your company page,
            team, and bookings.
          </p>
        </header>

        <form onSubmit={handleSubmit} className="application-form">
          <div className="application-grid">
            {OWNER_PRIMARY_FIELDS.map(([field, label]) => (
              <div key={field} className="field-group">
                <label htmlFor={field}>{label}:</label>
                <input
                  id={field}
                  type={field === 'dob' ? 'date' : field.includes('email') ? 'email' : 'text'}
                  value={formData[field]}
                  onChange={(e) => setFormData((prev) => ({ ...prev, [field]: e.target.value }))}
                  required
                />
              </div>
            ))}
            <div className="field-group field-wide">
              <label htmlFor="requested_company_name">Requested company name:</label>
              <input
                id="requested_company_name"
                type="text"
                value={formData.requested_company_name}
                onChange={(e) => setFormData((prev) => ({ ...prev, requested_company_name: e.target.value }))}
                required
              />
            </div>
          </div>

          <div className="password-row">
            <div className="field-group">
              <label htmlFor="password">Password:</label>
              <div className="password-input-wrap">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => setFormData((prev) => ({ ...prev, password: e.target.value }))}
                  required
                />
                <button
                  type="button"
                  className="password-toggle-btn"
                  onClick={() => setShowPassword((prev) => !prev)}
                >
                  {showPassword ? 'Hide' : 'Show'}
                </button>
              </div>
            </div>
            <div className="field-group">
              <label htmlFor="confirm_password">Confirm password:</label>
              <input
                id="confirm_password"
                type={showPassword ? 'text' : 'password'}
                value={formData.confirm_password}
                onChange={(e) => setFormData((prev) => ({ ...prev, confirm_password: e.target.value }))}
                required
              />
            </div>
          </div>

          <div className="application-grid application-section-spacer">
            {OWNER_COMPANY_FIELDS.map(([field, label]) => (
              <div key={field} className="field-group">
                <label htmlFor={field}>{label}:</label>
                <input
                  id={field}
                  type={field.includes('url') ? 'url' : field.includes('years') ? 'number' : 'text'}
                  value={formData[field]}
                  onChange={(e) => setFormData((prev) => ({ ...prev, [field]: e.target.value }))}
                />
              </div>
            ))}
            <div className="field-group field-wide">
              <label htmlFor="message">Message:</label>
              <textarea
                id="message"
                value={formData.message}
                onChange={(e) => setFormData((prev) => ({ ...prev, message: e.target.value }))}
                placeholder="Share anything helpful about your business setup and goals."
              />
            </div>
          </div>

          <div className="application-footer">
            <button type="submit" className="application-submit">Submit Owner Application</button>
            <Link to="/signup-worker" className="application-switch-link">Apply as worker instead</Link>
          </div>
          {message ? <p className="application-message">{message}</p> : null}
        </form>
      </div>
    </div>
  );
};

export default SignUpOwner;
